import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconLayer } from '@deck.gl/layers';
import { useInfrastructureClustering } from '@/hooks/useInfrastructureClustering';
import { generateClusterIconUrl } from '@/utils/clusterIconGenerator';
import { useSensorMaintenanceLens } from '../context/SensorMaintenanceLensContext';
import { generateSensorIcon } from '../utils/sensorIconGenerator';
import type { SensorDevice } from '../types/sensorMaintenanceLens.types';

const TRANSPARENT_ICON =
	'data:image/svg+xml;charset=utf-8,' +
	encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"></svg>');

interface UseSensorMaintenanceLayersParams {
	enabled: boolean;
	zoom: number;
	onDeviceClick?: (device: SensorDevice) => void;
	onDeviceHover?: (device: SensorDevice | null, x: number, y: number) => void;
	onClusterClick?: (longitude: number, latitude: number, expansionZoom: number) => void;
}

interface UseSensorMaintenanceLayersResult {
	layers: any[];
	onMapClick: (event: any) => boolean;
}

interface AdaptedDevice {
	longitude: number;
	latitude: number;
	measurementeInfraestructureId: string;
	mainMeasurementTarget: { expired: boolean; usage: number };
	_sensorDevice: SensorDevice;
}

function statusToMeasurementTarget(
	status: SensorDevice['status'],
): { expired: boolean; usage: number } {
	if (status === 'withoutData') return { expired: true, usage: 0 };
	if (status === 'inactive') return { expired: false, usage: 150 };
	return { expired: false, usage: 0 };
}

function adaptDevice(device: SensorDevice): AdaptedDevice {
	return {
		longitude: device.longitude,
		latitude: device.latitude,
		measurementeInfraestructureId: device.deviceId,
		mainMeasurementTarget: statusToMeasurementTarget(device.status),
		_sensorDevice: device,
	};
}

function deviceIconKey(device: SensorDevice): string {
	return device.status;
}

export function useSensorMaintenanceLayers({
	enabled,
	zoom,
	onDeviceClick,
	onDeviceHover,
	onClusterClick,
}: UseSensorMaintenanceLayersParams): UseSensorMaintenanceLayersResult {
	const { visibleDevices } = useSensorMaintenanceLens();
	const [icons, setIcons] = useState<Record<string, string>>({});

	const adaptedDevices = useMemo<AdaptedDevice[]>(
		() =>
			enabled
				? visibleDevices.filter(
						(device) => Number.isFinite(device.latitude) && Number.isFinite(device.longitude),
					).map(adaptDevice)
				: [],
		[enabled, visibleDevices],
	);

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		const pending = new Map<string, SensorDevice>();
		for (const adapted of adaptedDevices) {
			const key = deviceIconKey(adapted._sensorDevice);
			if (!pending.has(key)) pending.set(key, adapted._sensorDevice);
		}
		Promise.all(
			[...pending.entries()].map(([key, device]) =>
				generateSensorIcon(device.status).then((url) => [key, url] as const),
			),
		)
			.then((entries) => {
				if (!cancelled) setIcons((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
			})
			.catch((error) => {
				console.error('Error generating sensor icons:', error);
			});
		return () => {
			cancelled = true;
		};
	}, [enabled, adaptedDevices]);

	const { clusters, unclustered, getExpansionZoom } = useInfrastructureClustering(adaptedDevices, zoom);

	const layers = useMemo(() => {
		if (!enabled || adaptedDevices.length === 0) return [];

		const result: any[] = [];

		const unclusteredData = unclustered.map(({ infrastructure }) => ({
			...infrastructure,
			position: [infrastructure.longitude, infrastructure.latitude],
		}));

		result.push(
			new IconLayer({
				id: 'sensor-icon-layer',
				data: unclusteredData,
				pickable: true,
				getPosition: (d: any) => d.position,
				getIcon: (d: any) => ({
					url: icons[deviceIconKey(d._sensorDevice)] ?? TRANSPARENT_ICON,
					width: 200,
					height: 200,
				}),
				sizeScale: 1,
				getSize: Math.max(28, 46 * (zoom / 11)),
				onHover: (info: any) => {
					if (info?.object?._sensorDevice) {
						onDeviceHover?.(info.object._sensorDevice, info.x, info.y);
					} else {
						onDeviceHover?.(null, info?.x ?? 0, info?.y ?? 0);
					}
				},
				updateTriggers: {
					getIcon: [icons],
					getSize: [zoom],
				},
			}),
		);

		if (clusters.length > 0) {
			const clusterData = clusters.map((cluster) => ({
				...cluster,
				position: [cluster.longitude, cluster.latitude],
				_isSensorCluster: true,
			}));

			result.push(
				new IconLayer({
					id: 'sensor-cluster-layer',
					data: clusterData,
					pickable: true,
					getPosition: (d: any) => d.position,
					getIcon: (d: any) => ({
						url: generateClusterIconUrl({
							count: d.count,
							normalCount: d.normalCount,
							criticalCount: d.criticalCount,
							noDataCount: d.noDataCount,
							selectedCount: 0,
						}),
						width: 200,
						height: 200,
					}),
					sizeScale: 1,
					getSize: (d: any) => {
						const baseSize = Math.max(18, 34 * (zoom / 10));
						const countScale = 1 + Math.min(1.4, Math.log2(Math.max(1, d.count)) * 0.26);
						return baseSize * countScale;
					},
					updateTriggers: {
						getIcon: [clusters],
						getSize: [zoom],
					},
				}),
			);
		}

		return result;
	}, [enabled, adaptedDevices.length, unclustered, clusters, icons, zoom, onDeviceHover]);

	const onMapClick = useCallback(
		(event: any): boolean => {
			if (!enabled) return false;
			if (event?.picked && event.object?._isSensorCluster && event.layer?.id === 'sensor-cluster-layer') {
				const expansionZoom = getExpansionZoom(event.object.clusterId) + 1;
				onClusterClick?.(event.object.longitude, event.object.latitude, expansionZoom);
				return true;
			}
			if (event?.picked && event.object?._sensorDevice && event.layer?.id === 'sensor-icon-layer') {
				onDeviceClick?.(event.object._sensorDevice);
				return true;
			}
			return false;
		},
		[enabled, getExpansionZoom, onClusterClick, onDeviceClick],
	);

	return { layers, onMapClick };
}
